import { useRef, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { DraftState, RankingsImportResponse } from '../types/protocol'

interface Props {
  draft: DraftState
  commissionerToken: string
  // Required, not optional: the draft's rankings_count is what this section
  // renders from, so a caller that forgets to hand the new count back leaves
  // the page showing a count the server no longer agrees with.
  onCountChange: (count: number) => void
}

// CSV rankings override the pool's order for this draft only. An upload
// replaces whatever was there before — there is no merging of two files.
export default function RankingsUpload({ draft, commissionerToken, onCountChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [report, setReport] = useState<RankingsImportResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showMatched, setShowMatched] = useState(false)

  const count = draft.rankings_count

  async function upload(file: File) {
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const response = await api<RankingsImportResponse>(`/drafts/${draft.join_code}/rankings`, {
        method: 'POST',
        body: form,
        commissionerToken,
      })
      setReport(response)
      setShowMatched(false)
      onCountChange(response.count)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not read that file')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function clear() {
    setBusy(true)
    setError(null)
    try {
      await api<RankingsImportResponse>(`/drafts/${draft.join_code}/rankings`, {
        method: 'DELETE',
        commissionerToken,
      })
      setReport(null)
      onCountChange(0)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not clear the rankings')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="panel p-6">
      <h2 className="eyebrow">Rankings</h2>
      <p className="mt-1 text-sm text-ink-mid">
        Upload a FantasyPros-style CSV to set the player order for this draft. Anyone the file
        leaves out keeps their default rank and sorts to the bottom.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) void upload(file)
          }}
          className="max-w-full text-sm text-ink-mid file:mr-3 file:rounded file:border-0 file:bg-surface-3 file:px-3 file:py-1.5 file:text-ink-hi"
        />
        {count > 0 && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void clear()}
            className="text-sm text-ink-mid underline-offset-2 hover:underline disabled:opacity-50"
          >
            Clear rankings
          </button>
        )}
      </div>

      {busy && <p className="mt-3 text-sm text-ink-lo">Reading the file…</p>}
      {error && <p className="mt-3 text-sm text-crit">{error}</p>}

      {!report && !busy && count > 0 && (
        <p className="mt-3 text-sm text-signal">{count} players are using custom ranks.</p>
      )}

      {report && (
        <div className="mt-4 space-y-3 text-sm">
          <p className="text-signal">
            Matched {report.matched.length} of {report.matched.length + report.unmatched.length} rows.
          </p>

          {report.unmatched.length > 0 && (
            <div className="rounded-lg border border-warn/40 bg-warn/5 p-3">
              <p className="font-medium text-warn">
                {report.unmatched.length} row{report.unmatched.length === 1 ? '' : 's'} could not be
                matched and kept their default rank:
              </p>
              <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
                {report.unmatched.map((row) => (
                  <li key={row.line} className="text-ink-mid">
                    <span className="text-ink-lo">Line {row.line}:</span>{' '}
                    <span className="font-medium">{row.name || '(blank)'}</span>
                    {row.position ? ` (${row.position})` : ''} — {row.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report.matched.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowMatched((shown) => !shown)}
                className="text-ink-mid underline-offset-2 hover:underline"
              >
                {showMatched ? 'Hide' : 'Show'} the {report.matched.length} matched rows
              </button>
              {showMatched && (
                <ul className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                  {report.matched.map((row) => (
                    <li key={row.line} className="text-ink-mid">
                      <span className="text-ink-lo tabular-nums">{row.rank}.</span> {row.name} →{' '}
                      {row.player_name}{' '}
                      <span className="text-ink-lo">
                        ({row.player_position === 'DEF' ? 'DST' : row.player_position}
                        {row.player_team ? ` · ${row.player_team}` : ''})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
